/**
 * Google Drive Service for ChatBot
 * Handles token management and file operations for Google Drive integration
 */

export interface GoogleTokenData {
  token: string;
  refresh_token: string;
  token_uri: string;
  client_id: string;
  client_secret: string;
  scopes: string[];
  expiry: string;
}

export interface GoogleTokenResponse {
  google_token: GoogleTokenData;
}

export interface GoogleDriveConfig {
  clientId: string;
  clientSecret: string;
  apiKey: string;
  redirectUri: string;
}

export interface GoogleDrivePickerDoc {
  id: string;
  name: string;
  mimeType: string;
  lastEditedUtc?: number;
}

export interface GoogleDrivePickerData {
  action: string;
  docs?: GoogleDrivePickerDoc[];
}

/**
 * Refresh the Google access token using the refresh token
 */
export const refreshGoogleToken = async (
  refreshToken: string,
  config: GoogleDriveConfig
): Promise<{ accessToken: string; expiryTime: number; refreshToken: string } | null> => {
  try {
    const response = await fetch("https://oauth2.googleapis.com/token", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        client_id: config.clientId,
        client_secret: config.clientSecret,
        refresh_token: refreshToken,
        grant_type: "refresh_token",
      }),
    });

    const data = await response.json();

    if (data.error) {
      console.error("Error refreshing token:", data.error);
      return null;
    }

    const newAccessToken = data.access_token;
    const newExpiryTime = Date.now() + data.expires_in * 1000;

    // Store in localStorage for persistence
    localStorage.setItem("access_token", newAccessToken);
    localStorage.setItem("token_expiry", String(newExpiryTime));
    if (data.refresh_token) {
      localStorage.setItem("refresh_token", data.refresh_token);
    }

    return {
      accessToken: newAccessToken,
      expiryTime: newExpiryTime,
      refreshToken: data.refresh_token || refreshToken,
    };
  } catch (error) {
    console.error("Error refreshing access token:", error);
    return null;
  }
};

/**
 * Exchange authorization code for tokens
 */
export const exchangeAuthCode = async (
  code: string,
  config: GoogleDriveConfig
): Promise<GoogleTokenData | null> => {
  try {
    const response = await fetch("https://oauth2.googleapis.com/token", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        code: code,
        client_id: config.clientId,
        client_secret: config.clientSecret,
        redirect_uri: config.redirectUri,
        grant_type: "authorization_code",
        access_type: "offline",
        include_granted_scopes: "true",
      }),
    });

    const tokenData = await response.json();

    if (tokenData.error) {
      console.error("Error exchanging auth code:", tokenData.error);
      return null;
    }

    return {
      token: tokenData.access_token,
      refresh_token: tokenData.refresh_token || "",
      token_uri: "https://oauth2.googleapis.com/token",
      client_id: config.clientId,
      client_secret: config.clientSecret,
      scopes: tokenData.scope ? tokenData.scope.split(" ") : [],
      expiry: new Date(Date.now() + tokenData.expires_in * 1000).toISOString(),
    };
  } catch (error) {
    console.error("Error exchanging auth code:", error);
    return null;
  }
};

/**
 * Check if token is expired and refresh if needed
 */
export const handleTokenExpiration = async (
  currentToken: string | undefined,
  expiryTime: string | undefined,
  refreshToken: string | undefined,
  config: GoogleDriveConfig
): Promise<string | null> => {
  if (!currentToken) return null;

  const currentTime = Date.now();
  const tokenExpiry = expiryTime ? new Date(expiryTime).getTime() : 0;

  // If token is still valid, return it
  if (currentTime < tokenExpiry) {
    return currentToken;
  }

  // Token expired, try to refresh
  if (!refreshToken) {
    console.error("No refresh token available");
    return null;
  }

  const refreshedData = await refreshGoogleToken(refreshToken, config);
  return refreshedData?.accessToken || null;
};

/**
 * Get download URL for Google Drive file based on MIME type
 */
export const getFileDownloadUrl = (
  fileId: string,
  mimeType: string
): { url: string; newMimeType: string; extension: string } => {
  if (mimeType === "application/vnd.google-apps.spreadsheet") {
    return {
      url: `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`,
      newMimeType: "application/xlsx",
      extension: ".xlsx",
    };
  } else if (mimeType === "application/vnd.google-apps.document") {
    return {
      url: `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=application/vnd.openxmlformats-officedocument.wordprocessingml.document`,
      newMimeType: "application/docx",
      extension: ".docx",
    };
  } else {
    return {
      url: `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
      newMimeType: mimeType,
      extension: "",
    };
  }
};

/**
 * Download file from Google Drive
 */
export const downloadGoogleDriveFile = async (
  doc: GoogleDrivePickerDoc,
  accessToken: string
): Promise<File | null> => {
  try {
    const { url, newMimeType, extension } = getFileDownloadUrl(doc.id, doc.mimeType);
    const fileName = extension ? `${doc.name}${extension}` : doc.name;

    const response = await fetch(url, {
      headers: new Headers({
        Authorization: `Bearer ${accessToken}`,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${doc.name}, Status: ${response.status}`);
    }

    const blob = await response.blob();

    if (blob.size === 0) {
      throw new Error(`${doc.name} seems corrupted.`);
    }

    const mimeType = newMimeType || blob.type;

    return new File([blob], fileName, {
      type: mimeType,
      lastModified: doc.lastEditedUtc ? new Date(doc.lastEditedUtc).getTime() : Date.now(),
    });
  } catch (error) {
    console.error("Error downloading file:", error);
    return null;
  }
};

/**
 * Google Drive OAuth scopes required for picker
 */
export const GOOGLE_DRIVE_SCOPES = [
  "https://www.googleapis.com/auth/drive.file",
  "https://www.googleapis.com/auth/drive",
  "https://www.googleapis.com/auth/drive.metadata.readonly",
  "https://www.googleapis.com/auth/drive.metadata",
].join(" ");

/**
 * Load Google Picker API script
 */
export const loadGooglePickerScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.gapi?.picker) {
      resolve();
      return;
    }

    const apiScript = document.createElement("script");
    apiScript.src = "https://apis.google.com/js/api.js";
    apiScript.async = true;
    apiScript.onload = () => {
      window.gapi.load("picker", () => {
        resolve();
      });
    };
    apiScript.onerror = () => {
      reject(new Error("Failed to load Google Picker API"));
    };
    document.body.appendChild(apiScript);
  });
};

// Type augmentation for window object
declare global {
  interface Window {
    gapi: {
      load: (api: string, callback: () => void) => void;
      picker: any;
    };
    google: {
      picker: {
        PickerBuilder: new () => any;
        ViewId: {
          DOCS: string;
        };
        Feature: {
          MULTISELECT_ENABLED: string;
        };
        Action: {
          PICKED: string;
        };
      };
    };
  }
}

